import { create } from 'zustand';
import { createFileLogger } from '../utils/logger';
import useSpaceStore from './spaceStore';
import { useDesktopChatStore } from './desktopChatStore';
import { readChatPanelSpatialWidth } from '../utils/chatPanelSize';
import { getDockInsets, clampWindowGeometry, layoutFamilyOf } from '../components/Space/utils/windowUtils';
import type { OverviewCellRect } from '../components/Space/utils/overviewGrid';
import type { FlipRect } from '../components/Space/utils/windowMinimizeFlip';

const log = createFileLogger('SPATIAL_UI_STORE', { enabled: false });

declare global {
  interface Window {
    __windowDragging?: boolean;
    __lastDragUpdate?: number;
    __lastDragWindowId?: string;
    __lastDragPosition?: { x: number; y: number };
  }
}

export interface WindowState {
  position?: { x: number; y: number };
  size?: { width: number; height: number };
}

const windowDragState = {
  isDragging: false,
  pendingSave: false,
  lastDragTime: 0
};

// Debounce timer for hideFullscreenChromeDelayed — module-level so it survives
// across store updates without living in reactive state.
let fullscreenChromeHideTimer: ReturnType<typeof setTimeout> | null = null;

interface FocusMaximizeGeometry {
  position: { x: number; y: number };
  size: { width: number; height: number };
}

interface SpatialUIStore {
  windowStates: Record<string, WindowState>;
  windowZIndexes: Record<string, number>;

  /** Window IDs currently minimized (session-only, not persisted). */
  minimizedWindowIds: string[];
  /**
   * Screen-space rect of each minimized window's dock button, keyed by
   * windowId. Published continuously (every frame, while any window is
   * minimized) by DesktopDock so the real, live window can be persistently
   * transformed to sit inside it — no screenshotting, no ghosting: the
   * window itself is the dock preview. Also tracks the dock's own slide /
   * reflow animations 1:1 since it's re-measured live rather than captured
   * once. Read only for windows that are currently minimized.
   */
  minimizedSlotRects: Record<string, FlipRect>;
  /**
   * Window IDs whose minimized content is native-backed (webview / iframe /
   * video) and therefore can't be CSS live-scaled into the dock — those go
   * fully display:none and DesktopDock renders an app-icon fallback tile in
   * their slot. Non-native minimized windows are NOT listed here: their real,
   * live window is transformed on top of a transparent (empty) dock slot, so
   * no fallback tile is drawn. Classified + published by each Window.
   */
  minimizedNativeIds: string[];
  /**
   * The minimized window currently hovered, if any. Non-native minimized
   * windows stay live-scaled on top of their dock slot (see Window.tsx's
   * persistent live-scale minimize effect) in a portaled sibling layer, which
   * intercepts pointer events before they ever reach the dock button
   * underneath — so DesktopDock's own button-level hover state can't detect
   * the hover for those. Window.tsx's docked shield (the element that IS on
   * top) publishes hover state here instead, and DesktopDock reads it to show
   * the name tooltip regardless of which element the pointer actually lands on.
   */
  hoveredMinimizedWindowId: string | null;

  /** The window currently in "focus-maximize" mode (full-screen minus chat gutter). */
  focusMaximizedWindowId: string | null;
  /** Geometry saved before entering focus-maximize, used to restore. */
  prevFocusMaximizeGeometry: FocusMaximizeGeometry | null;

  /**
   * Whether the hover-revealed chrome (system header + window header) is
   * currently shown for a full-screen (focus-maximized) window. Session-only;
   * shared so the separately-mounted system header and window header reveal
   * and hide in sync when the user hovers the top edge of the screen.
   */
  fullscreenChromeRevealed: boolean;

  /**
   * macOS Mission-Control-style "show all windows" overview. Session-only: the
   * real, live windows are translated + scaled (via CSS transform, no
   * screenshotting) into a grid computed by SpaceOverview; selecting one
   * focuses the underlying window and exits. Never mutates window geometry —
   * the transform is purely visual and is cleared on exit.
   */
  overviewMode: boolean;
  /** Screen-space grid slot per window id, published by SpaceOverview once it measures
   *  the viewport. Windows read their own slot to drive their live-scale transform. */
  overviewRects: Record<string, OverviewCellRect>;
  /** Window ids whose content can't be live-scaled (native webview / iframe / video) —
   *  the live window hides for the duration and SpaceOverview draws an icon placeholder. */
  overviewNativeWindowIds: string[];
  /** The window id currently highlighted in the overview (keyboard nav or hover). */
  overviewSelectedWindowId: string | null;

  /**
   * Windows currently multi-selected via shift-click on their header. Session-only
   * (not persisted) — purely a UI affordance for the Cmd+G / Cmd+Shift+G grouping
   * flow (see gui/src/components/Space/utils/windowGrouping.ts and
   * docs/Interface/Space UI/WINDOW_GROUPING.md). Rendered as the `.window.selected`
   * outline in Window.tsx.
   */
  selectedWindowIds: string[];

  /** Add windowId to the selection if absent, remove it if present. */
  toggleWindowSelection: (windowId: string) => void;
  /** Replace the selection wholesale. */
  setWindowSelection: (windowIds: string[]) => void;
  /** Clear the selection (no-op, no log noise, if already empty). */
  clearWindowSelection: () => void;

  updateWindowPosition: (windowId: string, position: { x: number; y: number }) => void;
  updateWindowSize: (windowId: string, size: { width: number; height: number }) => void;
  setWindowState: (windowId: string, state: WindowState) => void;
  bringWindowToFront: (windowId: string) => void;
  finalizeWindowDrag: (
    windowId: string,
    position: { x: number; y: number },
    spaceId: string,
    onSave: () => Promise<void>
  ) => void;

  /** Mark window as minimized (hides from canvas, shows in dock tray). */
  minimizeWindow: (windowId: string) => void;
  /** Restore window from dock tray. */
  restoreWindow: (windowId: string) => void;
  /** Published by DesktopDock every frame while any window is minimized —
   *  screen-space dock-button rect per minimized window id. */
  setMinimizedSlotRects: (rects: Record<string, FlipRect>) => void;
  /** Published by each Window when it minimizes — whether its content is
   *  native-backed (needs the dock icon fallback) or live-scaled. */
  setMinimizedNative: (windowId: string, isNative: boolean) => void;
  /** Published by Window.tsx's docked shield on hover enter/leave (see
   *  `hoveredMinimizedWindowId` doc above). */
  setHoveredMinimizedWindow: (windowId: string | null) => void;

  /**
   * Toggle focus-maximize for the given window. If another window is already
   * focus-maximized it is first restored, then the new window is maximized.
   */
  toggleFocusMaximize: (windowId: string) => void;
  /**
   * Programmatically re-apply the maximized geometry for the current
   * focus-maximized window (e.g. after a viewport resize or chat toggle).
   */
  reapplyFocusMaximize: () => void;
  /** Exit focus-maximize without restoring geometry (e.g. window was closed). */
  clearFocusMaximize: () => void;
  /**
   * Exit focus-maximize, restoring the window's pre-maximize geometry first
   * when it still exists (e.g. the window was minimized rather than closed —
   * unlike clearFocusMaximize, the window will reappear later and shouldn't
   * come back at full-screen size).
   */
  exitFocusMaximizeRestoring: (windowId: string) => void;

  /** Reveal the full-screen chrome (system header + window header); cancels any pending hide. */
  revealFullscreenChrome: () => void;
  /** Hide the full-screen chrome after a short delay (debounced against re-reveal). */
  hideFullscreenChromeDelayed: () => void;

  /** Open the window overview overlay. Layout is published moments later by SpaceOverview. */
  enterOverview: () => void;
  /** Close the window overview overlay. */
  exitOverview: () => void;
  /** Published by SpaceOverview whenever the grid is (re)computed — screen-space slot
   *  per window id, plus which ids can't be live-scaled and need a placeholder card. */
  setOverviewLayout: (rects: Record<string, OverviewCellRect>, nativeIds: string[]) => void;
  /** Highlight a window in the overview (keyboard nav or hover over a live/placeholder card). */
  setOverviewSelected: (windowId: string | null) => void;

  /** Restore persisted z-order from CRDT window objects after space load. */
  hydrateZIndexesFromWindows: (windows: Record<string, { zIndex?: number; layouts?: { spatial?: { zIndex?: number }; spatialCanvas?: { zIndex?: number } } }>, family?: 'spatial' | 'spatialCanvas') => void;

  reset: () => void;
}

// ── Focus-maximize geometry helpers ──────────────────────────────────────────

function computeFocusMaximizeGeometry(): FocusMaximizeGeometry {
  // The system header is hidden while in focus-maximize mode, so topInset = 0.
  // Subtract the dock insets so the focus-maximized window stops above/beside
  // the dock — zero on both when the dock is in "Show on Hover" mode, since
  // that's a transient overlay that never reserves permanent space.
  const isChatVisible = useSpaceStore.getState().isChatVisible;
  const chatGutter = isChatVisible ? readChatPanelSpatialWidth() + 16 : 0;
  const dockInsets = getDockInsets();

  // Desktop's floating chat can be docked to any of the 4 corners (see
  // desktopChatStore.dockedCorner) — reserve the gutter on whichever side
  // it's actually pinned to, instead of always assuming the right rail.
  const { presentation, dockedCorner } = useDesktopChatStore.getState();
  const chatOnLeft = presentation === 'docked' && (dockedCorner === 'top-left' || dockedCorner === 'bottom-left');

  return {
    position: { x: dockInsets.left + (chatOnLeft ? chatGutter : 0), y: 0 },
    size: {
      width: window.innerWidth - chatGutter - dockInsets.left - dockInsets.right,
      height: window.innerHeight - dockInsets.bottom,
    },
  };
}

export const useSpatialUIStore = create<SpatialUIStore>((set, get) => ({
  windowStates: {},
  windowZIndexes: {},
  minimizedWindowIds: [],
  minimizedSlotRects: {},
  minimizedNativeIds: [],
  hoveredMinimizedWindowId: null,
  focusMaximizedWindowId: null,
  prevFocusMaximizeGeometry: null,
  fullscreenChromeRevealed: false,
  overviewMode: false,
  overviewRects: {},
  overviewNativeWindowIds: [],
  overviewSelectedWindowId: null,
  selectedWindowIds: [],

  toggleWindowSelection: (windowId: string) => {
    set((state) => {
      const isSelected = state.selectedWindowIds.includes(windowId);
      const next = isSelected
        ? state.selectedWindowIds.filter((id) => id !== windowId)
        : [...state.selectedWindowIds, windowId];
      log.log('[WINDOW_SELECTION] toggle', { windowId, wasSelected: isSelected, selection: next });
      return { selectedWindowIds: next };
    });
  },

  setWindowSelection: (windowIds: string[]) => {
    log.log('[WINDOW_SELECTION] set', { selection: windowIds });
    set({ selectedWindowIds: windowIds });
  },

  clearWindowSelection: () => {
    if (get().selectedWindowIds.length === 0) return;
    log.log('[WINDOW_SELECTION] clear');
    set({ selectedWindowIds: [] });
  },

  updateWindowPosition: (windowId: string, position: { x: number; y: number }) => {
    set(state => ({
      windowStates: {
        ...state.windowStates,
        [windowId]: {
          ...state.windowStates[windowId],
          position
        }
      }
    }));
  },
  
  updateWindowSize: (windowId: string, size: { width: number; height: number }) => {
    set(state => ({
      windowStates: {
        ...state.windowStates,
        [windowId]: {
          ...state.windowStates[windowId],
          size
        }
      }
    }));
  },
  
  setWindowState: (windowId: string, state: WindowState) => {
    set(store => ({
      windowStates: {
        ...store.windowStates,
        [windowId]: state
      }
    }));
  },
  
  bringWindowToFront: (windowId: string) => {
    const { windowZIndexes } = get();
    const values = Object.values(windowZIndexes);
    const max = values.length > 0 ? Math.max(...values) : 0;

    // Already on top — skip the z-raise and the CRDT round-trip. Still reconcile
    // the persisted mirror if it drifted: a prior compaction (or a skipped write)
    // can leave windows[id].zIndex lower than the live session z, which would make
    // a reload (z-order hydrated from persisted values) restore the wrong order.
    if (windowZIndexes[windowId] === max && max > 0) {
      const persistedZ = useSpaceStore.getState().windows[windowId]?.zIndex;
      if (persistedZ !== max) {
        useSpaceStore.getState().setWindowZIndex(windowId, max);
      }
      log.log(JSON.stringify({ event: '[Z_INDEX] spatialStore.bringWindowToFront:skipped_already_top', windowId, currentZ: windowZIndexes[windowId], max, allZIndexes: windowZIndexes }));
      return;
    }

    let next = { ...windowZIndexes, [windowId]: max + 1 };
    let wasCompacted = false;

    // Compact: renormalize to dense 1..N when values grow too large.
    // Threshold: any value exceeds 4× the window count.
    if (max + 1 > Object.keys(next).length * 4) {
      const sorted = Object.entries(next).sort((a, b) => a[1] - b[1]);
      next = {};
      sorted.forEach(([id], i) => { next[id] = i + 1; });
      wasCompacted = true;
    }

    log.log(JSON.stringify({ event: '[Z_INDEX] spatialStore.bringWindowToFront', windowId, prevZ: windowZIndexes[windowId] ?? null, newZ: next[windowId], max, wasCompacted, before: windowZIndexes, after: next }));

    const electron = (window as any).electron;
    if (electron?.BrowserView?.reorder) {
      electron.BrowserView.reorder(next);
    }

    set({ windowZIndexes: next });

    // Mirror the new z into windows[id].zIndex (via spaceStore, which also writes
    // the active layout-family slice) so reload hydration restores the live order.
    // Compaction renumbers EVERY window, so all of them must be persisted — not
    // just the focused one — otherwise the window that triggered the compaction
    // keeps its stale (large) persisted z and reloads at the bottom of the stack.
    const spaceStore = useSpaceStore.getState();
    if (wasCompacted) {
      for (const [id, z] of Object.entries(next)) {
        if (spaceStore.windows[id]?.zIndex !== z) {
          spaceStore.setWindowZIndex(id, z);
        }
      }
    } else {
      spaceStore.setWindowZIndex(windowId, next[windowId]);
    }
  },
  
  finalizeWindowDrag: (
    windowId: string,
    position: { x: number; y: number },
    spaceId: string,
    onSave: () => Promise<void>
  ) => {
    windowDragState.isDragging = false;
    
    if (!spaceId) return;

    // `spatial` is screen-space, so a dropped window can never be left
    // off-screen — but `allowUnderDock` deliberately lets it be dropped
    // under/behind a persistent dock (see clampWindowGeometry). `spatialCanvas`
    // is an infinite pannable world — panning off the visible viewport there
    // is normal, so it's left alone.
    const viewMode = useSpaceStore.getState().activeSpace?.settings?.viewMode;
    const win = useSpaceStore.getState().windows[windowId];
    const finalPosition =
      layoutFamilyOf(viewMode) === 'spatial' && win?.size
        ? clampWindowGeometry({ position, size: win.size }, { allowUnderDock: true }).position
        : position;

    get().updateWindowPosition(windowId, finalPosition);

    // Route through spaceStore.updateWindow so position+zIndex land in the
    // active layout-family slice together (single CRDT write, single updatedAt
    // stamp). This replaces the old direct spaceCRDTService.updateWindow call.
    const currentZ = get().windowZIndexes[windowId];
    const updates: Record<string, any> = { position: finalPosition };
    if (typeof currentZ === 'number') updates.zIndex = currentZ;

    log.log(JSON.stringify({ event: '[Z_INDEX] finalizeWindowDrag', windowId, position: finalPosition, currentZ: currentZ ?? null, allZIndexes: get().windowZIndexes }));

    useSpaceStore.getState().updateWindow(windowId, updates as any);
    
    if ((window as any).__lastDragWindowId === windowId) {
      delete (window as any).__lastDragWindowId;
      delete (window as any).__lastDragPosition;
    }
    
    windowDragState.pendingSave = false;
    
    onSave().catch(error => {
      log.error('[SpatialUIStore] Error saving window positions after drag:', error);
    });
  },
  
  toggleFocusMaximize: (windowId: string) => {
    const { focusMaximizedWindowId, prevFocusMaximizeGeometry } = get();
    const viewMode = useSpaceStore.getState().activeSpace?.settings?.viewMode;
    const isSpatialCanvas = viewMode === 'spatialCanvas';

    // ── Restore path ──────────────────────────────────────────────────────────
    if (focusMaximizedWindowId === windowId) {
      log.info('[SpatialUIStore] toggleFocusMaximize: restoring', windowId);
      if (!isSpatialCanvas && prevFocusMaximizeGeometry) {
        useSpaceStore.getState().updateWindow(windowId, prevFocusMaximizeGeometry);
      }
      if (fullscreenChromeHideTimer) {
        clearTimeout(fullscreenChromeHideTimer);
        fullscreenChromeHideTimer = null;
      }
      set({ focusMaximizedWindowId: null, prevFocusMaximizeGeometry: null, fullscreenChromeRevealed: false });
      return;
    }

    // If a different window is already focus-maximized, restore it first.
    if (focusMaximizedWindowId && prevFocusMaximizeGeometry && !isSpatialCanvas) {
      log.info('[SpatialUIStore] toggleFocusMaximize: restoring previous', focusMaximizedWindowId);
      useSpaceStore.getState().updateWindow(focusMaximizedWindowId, prevFocusMaximizeGeometry);
    }

    // ── Maximize path ─────────────────────────────────────────────────────────
    get().bringWindowToFront(windowId);

    if (isSpatialCanvas) {
      // In spatialCanvas, world coordinates ≠ screen coordinates so we don't
      // touch window geometry. Instead, dispatch the fit-window camera event
      // with the chat gutter so the canvas handler can adjust the viewport.
      const isChatVisible = useSpaceStore.getState().isChatVisible;
      const chatGutter = isChatVisible ? readChatPanelSpatialWidth() + 16 : 0;
      log.info('[SpatialUIStore] toggleFocusMaximize (canvas): fit-window with gutter', windowId, chatGutter);
      set({ focusMaximizedWindowId: windowId, prevFocusMaximizeGeometry: null });
      window.dispatchEvent(new CustomEvent('river:spatial-canvas-fit-window', {
        detail: { windowId, chatGutter },
      }));
      return;
    }

    const win = useSpaceStore.getState().windows[windowId];
    if (!win) return;

    // Clamp the saved (pre-maximize) geometry so an already-oversized or
    // under-dock window is shrunk to fit on restore — it can never be sent back
    // off-screen / under the dock.
    const saved: FocusMaximizeGeometry = clampWindowGeometry({
      position: { ...win.position },
      size: { ...win.size },
    });
    const target = computeFocusMaximizeGeometry();

    log.info('[SpatialUIStore] toggleFocusMaximize: maximizing', windowId, target);
    set({ focusMaximizedWindowId: windowId, prevFocusMaximizeGeometry: saved });
    useSpaceStore.getState().updateWindow(windowId, target);
  },

  reapplyFocusMaximize: () => {
    const { focusMaximizedWindowId } = get();
    if (!focusMaximizedWindowId) return;
    const target = computeFocusMaximizeGeometry();
    log.info('[SpatialUIStore] reapplyFocusMaximize', focusMaximizedWindowId, target);
    useSpaceStore.getState().updateWindow(focusMaximizedWindowId, target);
  },

  clearFocusMaximize: () => {
    log.info('[SpatialUIStore] clearFocusMaximize');
    if (fullscreenChromeHideTimer) {
      clearTimeout(fullscreenChromeHideTimer);
      fullscreenChromeHideTimer = null;
    }
    set({ focusMaximizedWindowId: null, prevFocusMaximizeGeometry: null, fullscreenChromeRevealed: false });
  },

  exitFocusMaximizeRestoring: (windowId: string) => {
    const { prevFocusMaximizeGeometry } = get();
    const viewMode = useSpaceStore.getState().activeSpace?.settings?.viewMode;
    const isSpatialCanvas = viewMode === 'spatialCanvas';
    if (!isSpatialCanvas && prevFocusMaximizeGeometry) {
      log.info('[SpatialUIStore] exitFocusMaximizeRestoring: restoring', windowId, prevFocusMaximizeGeometry);
      useSpaceStore.getState().updateWindow(windowId, prevFocusMaximizeGeometry);
    }
    get().clearFocusMaximize();
  },

  revealFullscreenChrome: () => {
    if (fullscreenChromeHideTimer) {
      clearTimeout(fullscreenChromeHideTimer);
      fullscreenChromeHideTimer = null;
    }
    if (!get().fullscreenChromeRevealed) {
      set({ fullscreenChromeRevealed: true });
    }
  },

  hideFullscreenChromeDelayed: () => {
    if (fullscreenChromeHideTimer) clearTimeout(fullscreenChromeHideTimer);
    fullscreenChromeHideTimer = setTimeout(() => {
      fullscreenChromeHideTimer = null;
      set({ fullscreenChromeRevealed: false });
    }, 300);
  },

  minimizeWindow: (windowId: string) => {
    log.info('[SpatialUIStore] minimizeWindow', windowId);
    set((state) => ({
      minimizedWindowIds: state.minimizedWindowIds.includes(windowId)
        ? state.minimizedWindowIds
        : [...state.minimizedWindowIds, windowId],
    }));
  },

  restoreWindow: (windowId: string) => {
    log.info('[SpatialUIStore] restoreWindow', windowId);
    set((state) => ({
      minimizedWindowIds: state.minimizedWindowIds.filter((id) => id !== windowId),
      minimizedNativeIds: state.minimizedNativeIds.filter((id) => id !== windowId),
      hoveredMinimizedWindowId: state.hoveredMinimizedWindowId === windowId ? null : state.hoveredMinimizedWindowId,
    }));
    // Restoring a window raises it to the top of the stack — a minimized window
    // is, by definition, one the user just chose to return to. Bumps the
    // session z-index (windowZIndexes, what actually drives stacking) here so
    // both restore entry points (dock click, window control) get it for free;
    // callers separately set activeWindowId via spaceStore.bringWindowToFront.
    get().bringWindowToFront(windowId);
  },

  setMinimizedSlotRects: (rects: Record<string, FlipRect>) => {
    set({ minimizedSlotRects: rects });
  },

  setMinimizedNative: (windowId: string, isNative: boolean) => {
    const { minimizedNativeIds } = get();
    const has = minimizedNativeIds.includes(windowId);
    if (isNative && !has) {
      set({ minimizedNativeIds: [...minimizedNativeIds, windowId] });
    } else if (!isNative && has) {
      set({ minimizedNativeIds: minimizedNativeIds.filter((id) => id !== windowId) });
    }
  },

  setHoveredMinimizedWindow: (windowId: string | null) => {
    if (get().hoveredMinimizedWindowId === windowId) return;
    set({ hoveredMinimizedWindowId: windowId });
  },

  enterOverview: () => {
    if (get().overviewMode) return;
    log.info('[SpatialUIStore] enterOverview');
    // The grid isn't known yet (it depends on the overlay's measured viewport,
    // computed by SpaceOverview on mount) — windows pick up their live-scale
    // transform the instant setOverviewLayout publishes it, typically the same frame.
    set({ overviewMode: true, overviewRects: {}, overviewNativeWindowIds: [], overviewSelectedWindowId: null });
  },

  exitOverview: () => {
    if (!get().overviewMode) return;
    log.info('[SpatialUIStore] exitOverview');
    set({ overviewMode: false, overviewRects: {}, overviewNativeWindowIds: [], overviewSelectedWindowId: null });
  },

  setOverviewLayout: (rects, nativeIds) => {
    if (!get().overviewMode) return;
    set({ overviewRects: rects, overviewNativeWindowIds: nativeIds });
  },

  setOverviewSelected: (windowId) => {
    set({ overviewSelectedWindowId: windowId });
  },

  /** Called on space init or mode switch to restore z-order for the given layout family.
   *  Assigns dense sequential z-values (1..N) sorted by the stored z so no two windows
   *  share the same value and windows lacking a stored z get a unique slot.
   *  When `family` is provided the family-slice zIndex is preferred over top-level. */
  hydrateZIndexesFromWindows: (windows: Record<string, { zIndex?: number; layouts?: { spatial?: { zIndex?: number }; spatialCanvas?: { zIndex?: number } } }>, family?: 'spatial' | 'spatialCanvas') => {
    const raw: { id: string; z: number }[] = [];
    for (const [id, w] of Object.entries(windows)) {
      const z = family ? (w.layouts?.[family]?.zIndex ?? w.zIndex) : w.zIndex;
      raw.push({ id, z: typeof z === 'number' ? z : 0 });
    }
    if (raw.length === 0) return;

    // Sort by existing z (ascending) then assign dense sequential 1..N.
    // Windows sharing a z value or lacking one get adjacent unique slots.
    raw.sort((a, b) => a.z - b.z);
    const normalized: Record<string, number> = {};
    raw.forEach(({ id }, i) => { normalized[id] = i + 1; });

    log.log(JSON.stringify({ event: '[Z_INDEX] hydrateZIndexesFromWindows', family: family ?? 'top-level', windowCount: raw.length, inputRaw: raw.map(({ id, z }) => ({ id, z })), normalizedOutput: normalized }));

    set({ windowZIndexes: normalized });
    log.info('[SpatialUIStore] Restored zIndexes from CRDT:', Object.keys(normalized).length, `windows${family ? ` (family=${family})` : ''} — compacted to dense 1..N`);
  },

  reset: () => {
    log.log(JSON.stringify({ event: '[Z_INDEX] spatialStore.reset' }));
    if (fullscreenChromeHideTimer) {
      clearTimeout(fullscreenChromeHideTimer);
      fullscreenChromeHideTimer = null;
    }
    set({
      windowStates: {},
      windowZIndexes: {},
      minimizedWindowIds: [],
      minimizedSlotRects: {},
      minimizedNativeIds: [],
      hoveredMinimizedWindowId: null,
      focusMaximizedWindowId: null,
      prevFocusMaximizeGeometry: null,
      fullscreenChromeRevealed: false,
      overviewMode: false,
      overviewRects: {},
      overviewNativeWindowIds: [],
      overviewSelectedWindowId: null,
      selectedWindowIds: [],
    });
  }
}));

// Expose a live dump for console debugging: useSpatialUIStore.getState().windowZIndexes
// or call: window.__zDump()
if (typeof window !== 'undefined') {
  (window as any).__zDump = () => {
    const s = useSpatialUIStore.getState();
    const spaceWins = (useSpaceStore as any).getState?.().windows ?? {};
    const rows = Object.entries(s.windowZIndexes).map(([id, z]) => ({
      id,
      z,
      appType: spaceWins[id]?.appType ?? '?',
      isActive: (useSpaceStore as any).getState?.().activeWindowId === id,
    }));
    rows.sort((a, b) => b.z - a.z);
    console.table(rows);
    log.log(JSON.stringify({ event: '[Z_INDEX] __zDump', activeWindowId: (useSpaceStore as any).getState?.().activeWindowId, windowZIndexes: s.windowZIndexes, minimizedWindowIds: s.minimizedWindowIds }));
  };
}

export default useSpatialUIStore;


